<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { getApiErrorMessage } from "@/helpers/error";
import interviewService from "@/modules/worker/services/interviewService";
import workerService from "@/modules/worker/services/workerService";
import { CVType } from '@/types/worker';

import type { TabGroupProps } from "@/dsComponents/Tab/types";
import type { InterviewAiSummaryPayload, InterviewReport } from "@/modules/worker/services/types";
import type { WorkerDetail } from "@/modules/worker/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Loader from "@/dsComponents/Loader.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import Tab from "@/dsComponents/Tab/Tab.vue";

import InterviewSummaryReviewModal from "@/components/modal/interviewReport/InterviewSummaryReviewModal.vue";
import InterviewTab from "@/components/modal/interviewReport/tabs/InterviewTab.vue";
import PreferencesTab from "@/components/modal/interviewReport/tabs/PreferencesTab.vue";
import QualificationsTab from "@/components/modal/interviewReport/tabs/QualificationsTab.vue";
import PdfManager from "@/components/PdfManager.vue";

interface InterviewReportModalProps {
    worker: WorkerDetail;
    workerId: string;
    onSubmit?: (interview: InterviewReport) => void;
    open?: boolean;
    refreshWorker?: () => Promise<void>;
}

const props = withDefaults(defineProps<InterviewReportModalProps>(), {
    open: true,
});

const emit = defineEmits<{
    close: [event?: MouseEvent | KeyboardEvent];
}>();

const { t } = useI18n();
const toaster = useToast();

const activeTab = ref<string>("interview");
const interviewContent = ref("");
const preferences = ref<Record<string, unknown>>({});
const qualifications = ref<Record<string, unknown>>({});
const summary = ref("");
const isGeneratingSummary = ref(false);
const isSaving = ref(false);
const isSummaryReviewOpen = ref(false);

const isInterviewContentEmpty = computed(() => interviewContent.value.trim().length === 0);
const hasUnsavedChanges = computed(() => !isInterviewContentEmpty.value);
const isWarningOpen = ref(false);

const cvSignedUrl = ref<string | null>(null);
const isCvLoading = ref(false);

const fetchMainCvSignedUrl = async () => {
    const cvPath = props.worker.cv?.path;
    if (!cvPath) {
        cvSignedUrl.value = null;
        return;
    }

    isCvLoading.value = true;
    try {
        const cvs = await workerService.getCvs(props.workerId);
        const mainCv = cvs.find((cv) => cv.type === CVType.MAIN);
        cvSignedUrl.value = mainCv?.signedUrl ?? null;
    } catch {
        cvSignedUrl.value = null;
    } finally {
        isCvLoading.value = false;
    }
};

onMounted(() => {
    void fetchMainCvSignedUrl();
});

const resolveCvParsedData = (): string => {
    const extended = props.worker as WorkerDetail & { cvParsedData?: string; cv_parsed_data?: string };
    const value = extended.cvParsedData ?? extended.cv_parsed_data;
    return typeof value === "string" ? value : "";
};

const tabGroups = computed<TabGroupProps[]>(() => [
    {
        tabItems: [
            {
                id: "interview",
                label: t("page.interim.profile.interview.tab.interview"),
                isActive: activeTab.value === "interview",
            },
            {
                id: "preferences",
                label: t("page.interim.profile.interview.tab.preferences"),
                isActive: activeTab.value === "preferences",
            },
            {
                id: "qualifications",
                label: t("page.interim.profile.interview.tab.qualifications"),
                isActive: activeTab.value === "qualifications",
            },
        ],
    },
]);

const handleTabSelect = (tabId: string | number) => {
    activeTab.value = String(tabId);
};

const handleGenerateSummary = async () => {
    isGeneratingSummary.value = true;
    try {
        const payload: InterviewAiSummaryPayload = {
            interview_qa: interviewContent.value,
            cv_parsed_data: resolveCvParsedData(),
            overall_opinion: "",
        };
        const result = await interviewService.generateAiSummary(props.workerId, payload);
        summary.value = result.summary;
        isSummaryReviewOpen.value = true;
    } catch (e) {
        console.error("Error generating interview summary:", e);
        toaster?.open({ message: getApiErrorMessage(e), type: "error", duration: 5000 });
    } finally {
        isGeneratingSummary.value = false;
    }
};

const handleCloseSummaryReview = () => {
    isSummaryReviewOpen.value = false;
};

const handleValidateSummary = async () => {
    if (!summary.value.trim()) {
        toaster?.open({ message: t("toast.defaultError"), type: "error", duration: 3000 });
        return;
    }
    isSaving.value = true;
    try {
        const createdInterview = await interviewService.createInterview(props.workerId, {
            summary: summary.value,
        });
        toaster?.open({ message: t("toast.defaultSuccess"), type: "success", duration: 3000 });
        isSummaryReviewOpen.value = false;
        props.onSubmit?.(createdInterview);
        void props.refreshWorker?.();
        emit("close");
    } catch (e) {
        console.error("Error saving interview:", e);
        toaster?.open({ message: t("toast.defaultError"), type: "error", duration: 5000 });
    } finally {
        isSaving.value = false;
    }
};

const handleSaveWithoutSummary = async () => {
    isSaving.value = true;
    try {
        const createdInterview = await interviewService.createInterview(props.workerId, {
            summary: interviewContent.value,
        });
        toaster?.open({ message: t("toast.defaultSuccess"), type: "success", duration: 3000 });
        props.onSubmit?.(createdInterview);
        void props.refreshWorker?.();
        emit("close");
    } catch {
        toaster?.open({ message: t("toast.defaultError"), type: "error", duration: 5000 });
    } finally {
        isSaving.value = false;
    }
};

const handleMainClose = () => {
    if (isSummaryReviewOpen.value) {
        isSummaryReviewOpen.value = false;
        return;
    }
    if (hasUnsavedChanges.value) {
        isWarningOpen.value = true;
        return;
    }
    void props.refreshWorker?.();
    emit("close");
};

const handleCloseWarning = () => {
    isWarningOpen.value = false;
};

const handleContinueWithoutSaving = () => {
    isWarningOpen.value = false;
    void props.refreshWorker?.();
    emit("close");
};
</script>

<template>
    <Modal
        class="interview-report-modal-root"
        :open="open"
        width="1200px"
        height="760px"
        position="top"
        content-overflow="hidden"
        no-padding
        disable-overlay-close
    >
        <div class="interview-report-modal">
            <div class="interview-report-modal__row">
                <div class="interview-report-modal__cv">
                    <div class="interview-report-modal__cv-top">
                        <span class="interview-report-modal__cv-title typo-sm-medium">
                            {{ t("page.interim.profile.interview.new") }}
                        </span>
                    </div>
                    <div class="interview-report-modal__cv-scroll">
                        <div class="interview-report-modal__cv-frame">
                            <PdfManager v-if="cvSignedUrl" :path="cvSignedUrl" :readonly="true" :show-actions="false" />
                            <div v-else-if="isCvLoading" class="interview-report-modal__cv-loading">
                                <Loader size="lg" />
                            </div>
                            <div v-else class="interview-report-modal__no-cv">
                                <i class="ti ti-file-off" />
                                <span>{{ t("page.interim.noDocument") }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <aside class="interview-report-modal__aside">
                    <div class="interview-report-modal__tabs-wrap">
                        <Tab :tab-groups="tabGroups" :border-bottom="false" no-padding @select:tab="handleTabSelect" />
                        <ButtonAlt iconOnly="x" size="sm" type="neutral" @click="handleMainClose" />
                    </div>
                    <div class="interview-report-modal__aside-scroll">
                        <InterviewTab v-if="activeTab === 'interview'" v-model:content="interviewContent" :worker-id="workerId" />
                        <PreferencesTab
                            v-else-if="activeTab === 'preferences'"
                            :worker="worker"
                            :worker-id="workerId"
                            :refresh-worker="refreshWorker"
                            v-model:preferences="preferences"
                        />
                        <QualificationsTab
                            v-else-if="activeTab === 'qualifications'"
                            :worker="worker"
                            :refresh-worker="refreshWorker"
                            v-model:qualifications="qualifications"
                        />
                    </div>
                    <div class="interview-report-modal__aside-footer">
                        <ButtonAlt
                            full-width
                            size="md"
                            :variant="isInterviewContentEmpty ? 'filled' : 'stroke'"
                            type="neutral"
                            :label="t('page.interim.profile.interview.saveWithoutSummary')"
                            :loading="isSaving"
                            :disabled="isInterviewContentEmpty || isGeneratingSummary"
                            @click="handleSaveWithoutSummary"
                        />
                        <ButtonAlt
                            full-width
                            size="md"
                            variant="filled"
                            type="primary"
                            :label="t('page.interim.profile.interview.generateSummary')"
                            :loading="isGeneratingSummary"
                            :disabled="isInterviewContentEmpty || isSaving"
                            @click="handleGenerateSummary"
                        />
                    </div>
                </aside>
            </div>
        </div>
    </Modal>

    <Modal
        :open="isWarningOpen"
        :title="t('page.interim.profile.interview.warningModal.title')"
        position="center"
        width="420px"
        close
        action
        @close="handleCloseWarning"
    >
        <p class="interview-report-warning__content typo-sm-medium">
            {{ t("page.interim.profile.interview.warningModal.content") }}
        </p>

        <template #footer>
            <div class="interview-report-warning__footer">
                <ButtonAlt
                    :label="t('page.interim.profile.interview.warningModal.actions.continueWithoutSaving')"
                    type="neutral"
                    @click="handleContinueWithoutSaving"
                />
                <ButtonAlt
                    :label="t('page.interim.profile.interview.warningModal.actions.save')"
                    variant="filled"
                    :loading="isSaving"
                    @click="handleSaveWithoutSummary"
                />
            </div>
        </template>
    </Modal>

    <InterviewSummaryReviewModal
        v-model:summary-text="summary"
        :open="isSummaryReviewOpen"
        :is-saving="isSaving"
        @close="handleCloseSummaryReview"
        @back="handleCloseSummaryReview"
        @submit="handleValidateSummary"
    />
</template>

<style scoped lang="scss">
.interview-report-modal {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background-color: var(--bg-white-0, #ffffff);

    &__row {
        display: flex;
        flex: 1;
        min-height: 0;
    }

    &__cv {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        min-height: 0;
        background-color: var(--bg-lightest-50, #f6f7f9);
    }

    &__cv-top {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: flex-start;
        box-sizing: border-box;
        height: 49px;
        padding: 0 var(--spacing-3xl, 24px);
        gap: var(--spacing-xl, 16px);
        border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
    }

    &__cv-title {
        color: var(--text-main-900, #00061a);
    }

    &__cv-scroll {
        flex: 1;
        min-height: 0;
        overflow: auto;
        display: flex;
        justify-content: center;
        padding: var(--spacing-3xl, 24px) 0;
    }

    &__cv-frame {
        width: 600px;
        min-height: 400px;
        border-radius: var(--radius-md, 8px);
        background-color: var(--bg-white-0, #ffffff);
        border: 0.5px solid var(--stroke-soft-200, #dbdfe6);
        box-shadow: var(--regular-shadow-lg, 0px 16px 40px 0px #3238481a);
        overflow: auto;
    }

    &__no-cv {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 320px;
        gap: var(--spacing-md, 8px);
        color: var(--text-soft-400, #9297aa);

        i {
            font-size: 48px;
        }
    }

    &__cv-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 320px;
    }

    &__aside {
        display: flex;
        flex-direction: column;
        width: 420px;
        flex-shrink: 0;
        min-height: 0;
        border-left: 1px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
    }

    &__tabs-wrap {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        padding: 0 var(--spacing-3xl, 24px);
        height: 49px;
        border-bottom: 1px solid var(--stroke-soft-200, #dbdfe6);
    }

    &__aside-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: var(--spacing-3xl, 24px);
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl, 16px);
    }

    &__aside-footer {
        display: flex;
        gap: var(--spacing-lg, 12px);
        flex-shrink: 0;
        padding: var(--spacing-xl, 16px) var(--spacing-3xl, 24px);
        border-top: 1px solid var(--stroke-soft-200, #dbdfe6);
    }
}

.interview-report-warning {
    &__content {
        color: var(--text-sub-600, #494e61);
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-md, 8px);
    }
}
</style>

<style lang="scss">
.modal-card:has(.interview-report-modal) {
    overflow: hidden;
}
</style>
